import { useEffect, useState } from 'react';
import type { SbCategory } from '@/components/builder/sb/CategoryChips';
import type { SbQuestion } from '@/components/builder/sb/QuestionCards';
import { apiFetch } from '@/lib/api-client';

interface UseSbQuestionsParams {
  mode: 'category' | 'random';
  category?: SbCategory;
  excludeIds?: string[];
  limit?: number;
}

interface UseSbQuestionsResult {
  questions: SbQuestion[];
  isLoading: boolean;
  error: string | null;
}

// Module-level cache keyed by (mode, category, limit). Only populated for
// exclude-less fetches — "Weitere Fragen" fetches (excludeIds non-empty) are
// always fresh and never cached, so the pool cursor isn't short-circuited.
const cache = new Map<string, SbQuestion[]>();

function cacheKey(params: UseSbQuestionsParams): string {
  return `${params.mode}:${params.category ?? ''}:${params.limit ?? 5}`;
}

function buildQueryString(params: UseSbQuestionsParams): string {
  const search = new URLSearchParams({ mode: params.mode });
  if (params.category) search.set('category', params.category);
  if (params.excludeIds && params.excludeIds.length > 0) {
    search.set('excludeIds', params.excludeIds.join(','));
  }
  if (params.limit !== undefined) {
    search.set('limit', String(params.limit));
  }
  return search.toString();
}

export function useSbQuestions(params: UseSbQuestionsParams): UseSbQuestionsResult {
  const key = cacheKey(params);
  const queryString = buildQueryString(params);
  const hasExcludes = (params.excludeIds?.length ?? 0) > 0;
  const trackingKey = `${key}::${queryString}`;

  const [lastTrackingKey, setLastTrackingKey] = useState(trackingKey);
  const [fetched, setFetched] = useState<SbQuestion[]>([]);
  const [isFetching, setIsFetching] = useState(hasExcludes || !cache.has(key));
  const [error, setError] = useState<string | null>(null);

  // React "sync derived state during render" — resets fetched-result state
  // when effective params change. setState calls during render are permitted
  // as long as they're conditional on a change-detector like trackingKey.
  if (lastTrackingKey !== trackingKey) {
    setLastTrackingKey(trackingKey);
    setFetched([]);
    setIsFetching(hasExcludes || !cache.has(key));
    setError(null);
  }

  useEffect(() => {
    if (!hasExcludes && cache.has(key)) return;
    let cancelled = false;
    apiFetch<SbQuestion[]>(`/api/sb/questions?${queryString}`)
      .then((list) => {
        if (cancelled) return;
        if (!hasExcludes) cache.set(key, list);
        setFetched(list);
        setIsFetching(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Fragen konnten nicht geladen werden.');
        setIsFetching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [key, queryString, hasExcludes]);

  const questions = hasExcludes ? fetched : (cache.get(key) ?? fetched);

  return { questions, isLoading: isFetching, error };
}
